/**
 * The card as a PDF, made on the device (2026-10-10; a headless browser on the
 * server made it until then, which needed the pictures on the server).
 *
 * Each printed side is the very SVG the print page draws (card-pdf.tsx renders
 * it off screen). It is turned into a picture at 300 dpi - the browser draws
 * the SVG onto a canvas - and the two pictures become the two pages of the PDF.
 * A drawn SVG can load nothing from outside itself, so first everything it uses
 * goes inside it: the pictures as data, the fonts of the notes as data, and the
 * few looks that come from the style sheet as inline styles.
 *
 * At 300 dpi a US Letter side is 2550 x 3300 pixels: sharp in print, and within
 * what phones allow a canvas.
 */

export const DPI = 300;

/** The properties a printed sheet takes from the style sheet (styles.css .paper-print and the like). */
const STYLED = ['fill', 'fill-opacity', 'stroke', 'stroke-width', 'stroke-opacity', 'stroke-dasharray', 'opacity', 'display'];

const blobToDataUrl = (b: Blob) => new Promise<string>((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result as string);
  r.onerror = () => reject(r.error);
  r.readAsDataURL(b);
});
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const familyKey = (f: string) => f.trim().replace(/^["']|["']$/g, '').toLowerCase();

/** "U+0000-00FF, U+0131, U+4??" -> [from, to] pairs. */
function ranges(unicodeRange: string): [number, number][] {
  if (!unicodeRange.trim()) return [[0, 0x10ffff]];
  return unicodeRange.split(',').map((part) => {
    const p = part.trim().replace(/^u\+/i, '');
    if (p.includes('?')) return [parseInt(p.replace(/\?/g, '0'), 16), parseInt(p.replace(/\?/g, 'F'), 16)];
    const [a, b] = p.split('-');
    return [parseInt(a, 16), parseInt(b ?? a, 16)];
  });
}

/**
 * @font-face rules, with the font files inlined, for the families the sheet's
 * text uses - and of those, only the files that cover characters it has
 * (the bundled fonts come split by script: Latin, Greek, Arabic...).
 */
async function embeddedFonts(svgs: SVGSVGElement[]): Promise<string> {
  const families = new Set<string>();
  const chars = new Set<number>();
  for (const svg of svgs) {
    for (const t of svg.querySelectorAll('text')) {
      for (const f of getComputedStyle(t).fontFamily.split(',')) families.add(familyKey(f));
      for (const ch of t.textContent || '') chars.add(ch.codePointAt(0)!);
    }
  }
  if (!families.size) return '';
  const used = [...chars];
  const rules: string[] = [];
  for (const sheet of document.styleSheets) {
    let list: CSSRuleList;
    try { list = sheet.cssRules; } catch { continue; }       // another site's style sheet: not ours to read
    for (const rule of list) {
      if (!(rule instanceof CSSFontFaceRule)) continue;
      const st = rule.style;
      if (!families.has(familyKey(st.getPropertyValue('font-family')))) continue;
      const span = ranges(st.getPropertyValue('unicode-range'));
      if (!used.some((c) => span.some(([a, b]) => c >= a && c <= b))) continue;
      const src = /url\(\s*["']?([^"')]+)["']?\s*\)/.exec(st.getPropertyValue('src'))?.[1];
      if (!src) continue;
      const res = await fetch(new URL(src, sheet.href ?? document.baseURI));
      if (!res.ok) continue;
      const data = await blobToDataUrl(await res.blob());
      rules.push(`@font-face{font-family:${st.getPropertyValue('font-family')};font-style:${st.getPropertyValue('font-style') || 'normal'};` +
        `font-weight:${st.getPropertyValue('font-weight') || '400'};unicode-range:${st.getPropertyValue('unicode-range') || 'U+0-10FFFF'};` +
        `src:url(${data})}`);
    }
  }
  return rules.join('\n');
}

/** A standalone copy of one sheet's SVG at `px` x `py` pixels: styles inline, pictures and fonts inside. */
async function standalone(svg: SVGSVGElement, px: number, py: number, fonts: string): Promise<string> {
  const copy = svg.cloneNode(true) as SVGSVGElement;
  const from = [svg, ...svg.querySelectorAll('*')];
  const to = [copy, ...copy.querySelectorAll('*')];
  from.forEach((el, i) => {
    const cs = getComputedStyle(el);
    const target = to[i] as SVGElement;
    for (const p of STYLED) target.style.setProperty(p, cs.getPropertyValue(p));
  });
  copy.setAttribute('width', String(px));
  copy.setAttribute('height', String(py));
  copy.removeAttribute('class');
  for (const im of copy.querySelectorAll('image')) {
    const href = im.getAttribute('href');
    if (href && !href.startsWith('data:')) im.setAttribute('href', await blobToDataUrl(await (await fetch(href)).blob()));
  }
  if (fonts) {
    const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
    style.textContent = fonts;
    copy.insertBefore(style, copy.firstChild);
  }
  return new XMLSerializer().serializeToString(copy);
}

/** Draw a standalone SVG onto a white canvas and save it as JPEG. */
async function rasterize(svgText: string, px: number, py: number): Promise<Uint8Array> {
  const url = URL.createObjectURL(new Blob([svgText], { type: 'image/svg+xml' }));
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    const c = document.createElement('canvas');
    c.width = px;
    c.height = py;
    const g = c.getContext('2d')!;
    const draw = () => {
      g.fillStyle = '#ffffff';
      g.fillRect(0, 0, px, py);
      g.drawImage(img, 0, 0, px, py);
    };
    draw();
    // Safari can draw an SVG before the pictures inside it have decoded: give them a moment, draw again
    await sleep(150);
    draw();
    const blob = await new Promise<Blob | null>((r) => c.toBlob(r, 'image/jpeg', 0.92));
    c.width = 0;
    if (!blob) throw new Error('This browser could not make the PDF.');
    return new Uint8Array(await blob.arrayBuffer());
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** The two (or more) printed sides as JPEG pages, from their on-screen SVGs. */
export async function sheetsToJpegs(svgs: SVGSVGElement[], paper: { widthMm: number; heightMm: number }, dpi = DPI) {
  const px = Math.round((paper.widthMm / 25.4) * dpi), py = Math.round((paper.heightMm / 25.4) * dpi);
  const fonts = await embeddedFonts(svgs);
  const pages: Uint8Array[] = [];
  for (const svg of svgs) pages.push(await rasterize(await standalone(svg, px, py, fonts), px, py));
  return { pages, px, py };
}

/**
 * A PDF of whole-page JPEG pictures, written by hand: a catalogue, a page
 * tree, and per page an image (DCTDecode - the JPEG as it is) drawn to fill
 * the page. Nothing else is needed, and no library.
 */
export function jpegPdf(pages: Uint8Array[], px: number, py: number, paper: { widthMm: number; heightMm: number }, title: string): Blob {
  const wPt = (paper.widthMm / 25.4) * 72, hPt = (paper.heightMm / 25.4) * 72;
  const enc = new TextEncoder();
  const parts: Uint8Array[] = [];
  const offsets: number[] = [];
  let size = 0;
  const add = (x: string | Uint8Array) => {
    const b = typeof x === 'string' ? enc.encode(x) : x;
    parts.push(b);
    size += b.length;
  };
  const obj = (n: number, body: string | (() => void)) => {
    offsets[n] = size;
    add(`${n} 0 obj\n`);
    if (typeof body === 'string') add(body); else body();
    add('\nendobj\n');
  };
  // PDF strings in UTF-16BE, so any title survives
  const pdfText = (s: string) => `<FEFF${[...s].map((ch) => {
    const c = ch.codePointAt(0)!;
    if (c < 0x10000) return c.toString(16).padStart(4, '0');
    const v = c - 0x10000;
    return (0xd800 + (v >> 10)).toString(16) + (0xdc00 + (v & 0x3ff)).toString(16);
  }).join('').toUpperCase()}>`;

  add('%PDF-1.7\n%âãÏÓ\n');
  const first = 4;                                    // 1 catalogue, 2 pages, 3 info, then 3 objects a page
  const kids = pages.map((_, i) => `${first + i * 3} 0 R`).join(' ');
  // ask the viewer to print two-sided, flipped on the long edge, at actual size (Adobe Reader and
  // Edge set their print dialog from this; others ignore it - the print page explains both ways)
  obj(1, '<< /Type /Catalog /Pages 2 0 R /ViewerPreferences << /Duplex /DuplexFlipLongEdge /PrintScaling /None >> >>');
  obj(2, `<< /Type /Pages /Kids [${kids}] /Count ${pages.length} >>`);
  obj(3, `<< /Title ${pdfText(title)} /Producer (FoldMyCard) >>`);
  const f = (n: number) => n.toFixed(2);
  pages.forEach((jpeg, i) => {
    const page = first + i * 3, image = page + 1, content = page + 2;
    obj(page, `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${f(wPt)} ${f(hPt)}] ` +
      `/Resources << /XObject << /Im0 ${image} 0 R >> >> /Contents ${content} 0 R >>`);
    obj(image, () => {
      add(`<< /Type /XObject /Subtype /Image /Width ${px} /Height ${py} /ColorSpace /DeviceRGB /BitsPerComponent 8 ` +
        `/Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`);
      add(jpeg);
      add('\nendstream');
    });
    const draw = `q ${f(wPt)} 0 0 ${f(hPt)} 0 0 cm /Im0 Do Q`;
    obj(content, `<< /Length ${draw.length} >>\nstream\n${draw}\nendstream`);
  });
  const count = first + pages.length * 3;
  const xref = size;
  add(`xref\n0 ${count}\n0000000000 65535 f \n`);
  for (let n = 1; n < count; n++) add(`${String(offsets[n]).padStart(10, '0')} 00000 n \n`);
  add(`trailer\n<< /Size ${count} /Root 1 0 R /Info 3 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
  return new Blob(parts as BlobPart[], { type: 'application/pdf' });
}
